import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2, Lock, Mail, User, Users } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { getPasswordPolicyHints, validatePassword } from '../lib/passwordPolicy';
import '../styles/NewUserRegistration.css';

const CALLBACK_URL = `${window.location.origin}/auth/callback`;

function NewUserRegistration() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [sex, setSex] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const passwordCheck = useMemo(() => validatePassword(password), [password]);

  const canSubmit = useMemo(() => {
    return (
      email &&
      name &&
      sex &&
      password &&
      confirmPassword &&
      password === confirmPassword &&
      passwordCheck.ok
    );
  }, [confirmPassword, email, name, password, passwordCheck.ok, sex]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!supabase) {
      setError('Supabaseが設定されていません。');
      return;
    }

    const pwd = validatePassword(password);
    if (!pwd.ok) {
      setError(pwd.errors[0]);
      return;
    }
    if (password !== confirmPassword) {
      setError('パスワードが一致しません。');
      return;
    }

    setSubmitting(true);
    try {
      const cleanedName = name.replace(/\s/g, '');
      const { error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: CALLBACK_URL,
          data: {
            username: cleanedName,
            sex,
          },
        },
      });
      if (signUpError) throw signUpError;

      navigate('/verify-email', { replace: true, state: { email } });
    } catch (err) {
      const msg = (err?.message || '').toLowerCase();
      if (msg.includes('already registered') || msg.includes('already exists')) {
        setError('このメールアドレスは既に登録されています。');
      } else if (msg.includes('password')) {
        setError('パスワードが条件を満たしていません。');
      } else if (msg.includes('invalid') && msg.includes('email')) {
        setError('メールアドレスの形式が正しくありません。');
      } else {
        setError('ユーザー登録に失敗しました。');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="new-user-registration-page">
      <div className="registration-background"></div>

      <div className="registration-container">
        <header className="registration-header">
          <div className="registration-header-logo">
            {/* Logo text or simple branding if needed, CSS handles styling */}
            Clinical Clerkship
          </div>
          <Link to="/login" className="registration-back-link">
            ログインへ戻る
          </Link>
        </header>

        <main className="registration-card">
          <h1 className="registration-title">新規ユーザー登録</h1>
          <p className="registration-subtitle">
            登録後、確認メールのリンクをクリックして認証を完了してください。
          </p>

          {error && (
            <div className={`registration-auth-alert ${error ? 'error' : ''}`} role="alert">
              <AlertCircle size={18} />
              <div>{error}</div>
            </div>
          )}

          {!passwordCheck.ok && password && (
            <div className="registration-auth-alert error" role="alert">
              <AlertCircle size={18} />
              <div>{passwordCheck.errors[0]}</div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="registration-form">
            <div className="registration-form-group">
              <label htmlFor="email">大学メールアドレス</label>
              <div className="registration-input-wrapper">
                <Mail className="registration-input-icon" size={18} />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="h20m0000@hirosaki-u.ac.jp"
                />
              </div>
            </div>

            <div className="registration-form-group">
              <label htmlFor="name">氏名（スペースなし）</label>
              <div className="registration-input-wrapper">
                <User className="registration-input-icon" size={18} />
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value.replace(/\s/g, ''))}
                  required
                  autoComplete="name"
                  placeholder="山田太郎"
                />
              </div>
            </div>

            <div className="registration-form-group">
              <label htmlFor="sex">性別</label>
              <div className="registration-input-wrapper">
                <Users className="registration-input-icon" size={18} />
                <select
                  id="sex"
                  value={sex}
                  onChange={(e) => setSex(e.target.value)}
                  required
                >
                  <option value="">選択してください</option>
                  <option value="male">男性 (Male)</option>
                  <option value="female">女性 (Female)</option>
                </select>
              </div>
            </div>

            <div className="registration-form-group">
              <label htmlFor="password">
                パスワード
              </label>
              <div className="registration-input-wrapper">
                <Lock className="registration-input-icon" size={18} />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder="例: Abcdef12!@#..."
                />
              </div>
              <div className="registration-password-hint">
                {getPasswordPolicyHints().map((hint, i) => (
                  <span key={i} style={{ display: 'block' }}>• {hint}</span>
                ))}
              </div>
            </div>

            <div className="registration-form-group">
              <label htmlFor="confirmPassword">
                パスワード（確認）
              </label>
              <div className="registration-input-wrapper">
                <Lock className="registration-input-icon" size={18} />
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder="もう一度入力"
                />
              </div>
            </div>

            <button type="submit" disabled={!canSubmit || submitting} className="registration-submit-button">
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={18} style={{ marginRight: 8 }} />
                  登録中...
                </>
              ) : (
                '登録する'
              )}
            </button>
          </form>
        </main>
      </div>
    </div>
  );
}

export default NewUserRegistration;

